import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../config';
import i18n from '../i18n';

const tr = i18n.t.bind(i18n);

const Billing = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [error, setError] = useState('');

  const currentLang = useMemo(() => {
    const lng = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
    return lng.startsWith('el') ? 'el-GR' : 'en-GB';
  }, []);

  const fetchBilling = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(
        tr('{{API_BASE_URL}}/billing', {
          API_BASE_URL: config.API_BASE_URL
        }),
        { withCredentials: true }
      );
      setRecords(Array.isArray(res.data) ? res.data : []);
      window.dispatchEvent(new Event('estiasync:plan-refresh'));
    } catch (err) {
      console.error('Billing fetch error:', err);
      setError(tr('Failed to load billing data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBilling();
  }, []);

  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatDate = (v) => {
    if (!v) return '-';
    const s = String(v);
    return s.includes('T') ? s.split('T')[0] : s;
  };

  const formatMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '-';
    return `${n.toFixed(2)} €`;
  };

  const getTotal = (rec) => {
    if (rec.price !== undefined && rec.price !== null) return toNumber(rec.price);
    if (rec.cost !== undefined && rec.cost !== null) return toNumber(rec.cost);
    return 0;
  };

  const getPaid = (rec) => toNumber(rec.paid);

  const getDue = (rec) => {
    const due = getTotal(rec) - getPaid(rec);
    return due > 0 ? due : 0;
  };

  const isSettled = (rec) => getDue(rec) <= 0.00001;

  const getStatusText = (rec) => {
    if (isSettled(rec)) return tr('Paid');
    return tr('Unpaid');
  };

  const handlePay = async (rec) => {
    try {
      setError('');
      setPayingId(rec.id);

      const dueMinor = Math.round(getDue(rec) * 100);

      if (!dueMinor || dueMinor <= 0) {
        setError(tr('This charge is already paid.'));
        return;
      }

      const response = await axios.post(
        `${config.API_BASE_URL}/_payments/viva/create-order`,
        {
          subscriptionId: rec.id,
          amountMinor: dueMinor,
          requestLang: currentLang,
          countryCode: 'GR'
        },
        { withCredentials: true }
      );

      const checkoutUrl = response?.data?.checkoutUrl;
      if (!checkoutUrl) {
        throw new Error('Missing checkoutUrl from server');
      }

      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Viva create-order error:', err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        tr('Failed to start payment');
      setError(msg);
      setPayingId(null);
    }
  };

  return (
    <div className="responsive-container" style={{ margin: '40px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16
        }}
      >
        <h2 style={{ margin: 0 }}>{tr('Billing')}</h2>

        <button
          onClick={fetchBilling}
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid #d0d7de',
            background: '#fff',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          {loading ? tr('Refreshing...') : tr('Refresh')}
        </button>
      </div>

      {error ? (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#fff1f0',
            border: '1px solid #ffccc7',
            color: '#a8071a'
          }}
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <div style={{ padding: '18px 0' }}>{tr('Loading billing data...')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            cellPadding="8"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              textAlign: 'center',
              minWidth: 900
            }}
          >
            <thead>
              <tr>
                <th>#</th>
                <th>{tr('Charge')}</th>
                <th>{tr('Starts')}</th>
                <th>{tr('Ends')}</th>
                <th>{tr('Cost')}</th>
                <th>{tr('Paid')}</th>
                <th>{tr('Due')}</th>
                <th>{tr('Status')}</th>
                <th>{tr('Invoice')}</th>
                <th>{tr('Payment')}</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ padding: '24px 8px' }}>
                    {tr('No billing records found')}
                  </td>
                </tr>
              ) : (
                records.map((rec, i) => {
                  const total = getTotal(rec);
                  const paid = getPaid(rec);
                  const due = getDue(rec);
                  const settled = isSettled(rec);
                  const rowBusy = payingId === rec.id;

                  return (
                    <tr key={rec.id || i} style={{ borderTop: '1px solid #eee' }}>
                      <td>{i + 1}</td>
                      <td>{rec.type || '-'}</td>
                      <td>{formatDate(rec.starts)}</td>
                      <td>{formatDate(rec.ends)}</td>
                      <td>{total ? formatMoney(total) : '-'}</td>
                      <td>{total ? formatMoney(paid) : '-'}</td>
                      <td>{total ? formatMoney(due) : '-'}</td>
                      <td style={{ color: settled ? 'darkgreen' : '#b54708', fontWeight: 700 }}>
                        {settled ? '✓' : '•'} {getStatusText(rec)}
                      </td>
                      <td>
                        {rec.invoice ? (
                          <a
                            href={`${config.API_BASE_URL}/billing/invoice/${rec.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {'⇩'} {tr('Download')}
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {settled ? (
                          <span style={{ color: 'darkgreen', fontWeight: 600 }}>
                            {tr('Completed')}
                          </span>
                        ) : (
                          <button
                            onClick={() => handlePay(rec)}
                            disabled={rowBusy}
                            style={{
                              padding: '10px 14px',
                              borderRadius: 10,
                              border: 'none',
                              background: rowBusy ? '#c9c9c9' : '#111827',
                              color: '#fff',
                              cursor: rowBusy ? 'default' : 'pointer',
                              fontWeight: 700,
                              minWidth: 130
                            }}
                          >
                            {rowBusy ? tr('Redirecting...') : tr('Pay with Viva')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Billing;